import { useRef, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';
import { PAGE_SEO } from '../../lib/seo';
import { motion, useInView } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
    Zap,
    Phone,
    Truck,
    CheckCircle,
    ArrowRight,
    Clock,
    MessageSquare,
    Volume2,
    FileText,
    Bell,
    Eye,
    PhoneCall,
    Users,
    Mic
} from 'lucide-react';

const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

function AnimatedSection({ children, className = "", delay = 0 }) {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-80px" });
    return (
        <motion.div
            ref={ref}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={{
                hidden: { opacity: 0, y: 50 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay, ease: "easeOut" } }
            }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

export default function AIVoiceDispatchPage() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const softwareAppSchema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Towmatic Voice AI",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "description": "AI voice dispatch agent for towing companies. Answers calls 24/7, gathers job details through natural conversation, and dispatches drivers instantly.",
        "url": "https://towmatic.ai/ai-voice-agent-tow-dispatch",
        "offers": {
            "@type": "Offer",
            "url": "https://towmatic.ai/pricing"
        },
        "creator": {
            "@type": "Organization",
            "name": "Towmatic",
            "url": "https://towmatic.ai"
        }
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {
                "@type": "Question",
                "name": "Does the AI voice agent sound robotic?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "No. Towmatic uses advanced voice AI that engages in natural conversation. Most callers can't tell they're speaking with an AI."
                }
            },
            {
                "@type": "Question",
                "name": "Can the voice agent handle multiple calls at once?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. Unlike a human dispatcher, Towmatic can handle simultaneous calls without putting anyone on hold."
                }
            },
            {
                "@type": "Question",
                "name": "Does this replace my existing phone number?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "No. Towmatic works with your existing business number through call forwarding. No hardware changes required."
                }
            }
        ]
    };

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://towmatic.ai/" },
            { "@type": "ListItem", "position": 2, "name": "AI Voice Dispatch for Towing", "item": "https://towmatic.ai/ai-voice-agent-tow-dispatch" }
        ]
    };

    return (
        <div className="min-h-screen bg-black text-white overflow-x-hidden">
            <SEOHead
                title={PAGE_SEO.aiVoiceDispatchForTowing.title}
                description={PAGE_SEO.aiVoiceDispatchForTowing.description}
                keywords={PAGE_SEO.aiVoiceDispatchForTowing.keywords}
                canonicalUrl="https://towmatic.ai/ai-voice-agent-tow-dispatch"
                noIndex={PAGE_SEO.aiVoiceDispatchForTowing.noIndex}
            />
            <>
                <script type="application/ld+json">{JSON.stringify(softwareAppSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
            </>

            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-zinc-800/50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <img src="/towmatic-logo-white.png" alt="Towmatic AI Voice Dispatch" className="h-8" />
                    </Link>
                    <div className="hidden md:flex items-center gap-8">
                        <Link to="/ai-towing-dispatch" className="text-zinc-400 hover:text-white transition text-sm font-medium">AI Dispatch</Link>
                        <Link to="/after-hours-tow-answering-service" className="text-zinc-400 hover:text-white transition text-sm font-medium">After Hours</Link>
                        <Link to="/towing-dispatch-software-comparison" className="text-zinc-400 hover:text-white transition text-sm font-medium">Compare</Link>
                        <Link to="/pricing" className="text-zinc-400 hover:text-white transition text-sm font-medium">Pricing</Link>
                    </div>
                    <div className="flex items-center gap-4">
                        <Button onClick={() => navigate('/login')} variant="ghost" className="text-zinc-300 hover:text-white hidden sm:inline-flex">Login</Button>
                        <Button onClick={() => navigate('/signup')} className="bg-orange-500 hover:bg-orange-600 text-white px-6 font-semibold">Get Started</Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-600/20 via-transparent to-zinc-900/50" />
                
                <div className="relative z-10 max-w-7xl mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <div className="inline-flex items-center gap-2 bg-violet-500/20 border border-violet-500/30 rounded-full px-4 py-2 mb-6">
                                <Mic className="w-4 h-4 text-violet-400" />
                                <span className="text-violet-400 text-sm font-semibold">Voice AI Technology</span>
                            </div>

                            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-[1.1] tracking-tight">
                                AI Voice Dispatch That Actually{' '}
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-500">
                                    Understands Towing
                                </span>
                            </h2>

                            <p className="text-lg text-zinc-400 mb-8 leading-relaxed">
                                Towmatic's AI voice agent answers every call, gathers the details that matter, and dispatches your drivers — without missing a beat at 2 AM or during a storm surge. Anyone can build a voice bot. We built a dispatcher.
                            </p>

                            <div className="flex flex-wrap gap-4">
                                <Button
                                    onClick={() => navigate('/pricing')}
                                    size="lg"
                                    className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-8 py-6 text-lg rounded-xl font-bold"
                                    data-testid="hero-pricing-btn"
                                >
                                    See Our Pricing <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                                <Button
                                    onClick={() => navigate('/contact')}
                                    variant="outline"
                                    size="lg"
                                    className="border-zinc-700 text-white hover:bg-zinc-800 px-8 py-6 text-lg rounded-xl"
                                    data-testid="hero-contact-btn"
                                >
                                    Contact Us
                                </Button>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="absolute -inset-4 bg-gradient-to-r from-violet-500/30 to-purple-500/20 rounded-3xl blur-2xl" />
                            <img
                                src="https://images.pexels.com/photos/7709148/pexels-photo-7709148.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                alt="AI voice dispatch agent handling towing calls"
                                className="relative rounded-2xl shadow-2xl border border-zinc-800 w-full"
                            />
                            <motion.div
                                animate={{ y: [0, -10, 0] }}
                                transition={{ duration: 3, repeat: Infinity }}
                                className="absolute -bottom-6 -left-6 bg-zinc-900 border border-zinc-800 rounded-xl p-4 shadow-xl"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-violet-500/20 rounded-full flex items-center justify-center">
                                        <Volume2 className="w-6 h-6 text-violet-500" />
                                    </div>
                                    <div>
                                        <p className="text-white font-bold">Call Answered</p>
                                        <p className="text-zinc-500 text-sm">Gathering job details...</p>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* The Problem Section */}
            <section className="py-20 bg-gradient-to-b from-black to-zinc-900/50">
                <div className="max-w-4xl mx-auto px-6">
                    <AnimatedSection>
                        <h2 className="text-3xl sm:text-4xl font-black mb-6 tracking-tight">
                            A Voice Bot That Collects a Name and Address Isn't a Dispatcher
                        </h2>
                        <div className="space-y-6 text-lg text-zinc-400 leading-relaxed">
                            <p>
                                A lot of companies have seen AI explode and rushed to market with "voice bots" for towing. Most of them do the same thing: answer the phone, ask for a name and address, and dump that info in your inbox. Done.
                            </p>
                            <p>
                                But that's the easy 10% of the job. The other 90% is qualification — is this a lockout, a breakdown, a police tow, or an accident? — and then what happens after: dispatch logic, driver rotation, escalation, edge cases that only emerge after hundreds of real calls.
                            </p>
                            <p className="text-white font-semibold">
                                Towmatic's AI wasn't trained on generic scripts. It was trained on real tow calls, in real operations, by real tow company owners.
                            </p>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-6">
                    <AnimatedSection className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-black mb-6 tracking-tight">
                            From Ring to Rolling — Here's What Happens
                        </h2>
                    </AnimatedSection>

                    <motion.div
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="space-y-6"
                    >
                        {[
                            {
                                icon: Phone,
                                title: 'Instant Answer, Natural Conversation',
                                desc: "Your phone rings. Towmatic answers immediately — no hold music, no voicemail. The AI engages in natural conversation, confirming the caller's location, their vehicle, the service type, and what happened."
                            },
                            {
                                icon: Truck,
                                title: 'Instant Dispatch',
                                desc: "The moment the call ends, the job hits your dispatch system. Your drivers are notified via text, phone call, and email — all at the same time. The phone call is critical: at 3 AM, your driver isn't checking their inbox."
                            },
                            {
                                icon: Bell,
                                title: 'Automatic Escalation',
                                desc: "If the driver doesn't confirm, Towmatic doesn't wait. It moves to the next driver in your rotation, following the escalation rules you set. You're notified at every step."
                            }
                        ].map((item, idx) => (
                            <motion.div
                                key={idx}
                                variants={fadeInUp}
                                className="flex gap-6 items-start bg-zinc-900/30 border border-zinc-800 rounded-2xl p-6 hover:border-violet-500/30 transition-all"
                            >
                                <div className="w-16 h-16 bg-violet-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <item.icon className="w-8 h-8 text-violet-500" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                                    <p className="text-zinc-400">{item.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Real Calls Section */}
            <section className="py-20 bg-gradient-to-b from-zinc-900/50 to-black">
                <div className="max-w-4xl mx-auto px-6">
                    <AnimatedSection>
                        <h2 className="text-3xl sm:text-4xl font-black mb-6 tracking-tight">
                            We Studied How People Actually Talk When They Need a Tow
                        </h2>
                        <div className="space-y-6 text-lg text-zinc-400 leading-relaxed">
                            <p>
                                People calling for a tow aren't calm. They're on the side of a highway in the dark. They don't know the cross-street — they'll say "I'm near the gas station, you know, the one off the highway." They'll say "my car won't start" when they mean "my car was hit."
                            </p>
                            <p>
                                Towmatic has handled over 1,000 real dispatches. We've studied how callers phrase things, what shorthand they use, and the edge cases that only show up in the field. The AI isn't built for clean demos — it's built for the messy reality of a tow call.
                            </p>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* Web Forms Section */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <AnimatedSection>
                            <h2 className="text-3xl sm:text-4xl font-black mb-6 tracking-tight">
                                Some Customers Don't Want to Talk — We Built for That Too
                            </h2>
                            <div className="space-y-6 text-lg text-zinc-400 leading-relaxed">
                                <p>
                                    Not everyone wants to call. Some customers — especially younger ones — would rather request a tow without speaking to anyone. Towmatic includes instant quote and dispatch web forms that let customers enter their details, get a quote, and submit in seconds.
                                </p>
                                <p className="text-white font-semibold">
                                    The voice agent and web forms work together as one system — same dispatch logic, same driver notifications, same dashboard visibility.
                                </p>
                            </div>
                            <div className="mt-8">
                                <Button
                                    onClick={() => navigate('/web-forms')}
                                    variant="outline"
                                    className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10"
                                >
                                    Learn About Web Forms <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </div>
                        </AnimatedSection>
                        <AnimatedSection delay={0.2}>
                            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                                        <FileText className="w-6 h-6 text-orange-500" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white">Web Form Request</h3>
                                </div>
                                <div className="space-y-4">
                                    <div className="bg-zinc-800/50 rounded-lg p-4">
                                        <p className="text-zinc-500 text-sm">Vehicle</p>
                                        <p className="text-white">2019 Honda Accord</p>
                                    </div>
                                    <div className="bg-zinc-800/50 rounded-lg p-4">
                                        <p className="text-zinc-500 text-sm">Service</p>
                                        <p className="text-white">Jump Start</p>
                                    </div>
                                    <div className="bg-zinc-800/50 rounded-lg p-4">
                                        <p className="text-zinc-500 text-sm">Location</p>
                                        <p className="text-white">Walmart Parking Lot, Main St</p>
                                    </div>
                                    <div className="flex items-center gap-2 text-green-500">
                                        <CheckCircle className="w-5 h-5" />
                                        <span className="font-semibold">Dispatched to driver</span>
                                    </div>
                                </div>
                            </div>
                        </AnimatedSection>
                    </div>
                </div>
            </section>

            {/* Control Section */}
            <section className="py-20 bg-gradient-to-b from-black to-zinc-900/50">
                <div className="max-w-4xl mx-auto px-6">
                    <AnimatedSection>
                        <h2 className="text-3xl sm:text-4xl font-black mb-6 tracking-tight">
                            Every Call Logged, Every Detail Captured
                        </h2>
                        <div className="space-y-6 text-lg text-zinc-400 leading-relaxed">
                            <p>
                                All voice interactions are logged with full details — caller info, service type, timestamps, dispatch status, driver response (or lack of one). You're not flying blind. You see exactly what's happening across your operation from your dashboard.
                            </p>
                        </div>
                        <div className="mt-8">
                            <Button
                                onClick={() => navigate('/contact')}
                                size="lg"
                                className="bg-violet-500 hover:bg-violet-600 text-white px-8 py-6 text-lg rounded-xl"
                            >
                                Contact Us to Get Started <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20">
                <div className="max-w-4xl mx-auto px-6">
                    <AnimatedSection className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-black tracking-tight">Frequently Asked Questions</h2>
                    </AnimatedSection>

                    <div className="space-y-6">
                        {[
                            { q: "Does the AI voice agent sound robotic?", a: "No. Towmatic uses advanced voice AI that engages in natural conversation. Most callers can't tell they're speaking with an AI." },
                            { q: "Can the voice agent handle multiple calls at once?", a: "Yes. Unlike a human dispatcher, Towmatic can handle simultaneous calls without putting anyone on hold." },
                            { q: "What if a caller has a complicated situation?", a: "Towmatic is built for edge cases. If a situation is truly outside the AI's scope, it can escalate to you via phone call immediately." },
                            { q: "Does this replace my existing phone number?", a: "No. Towmatic works with your existing business number through call forwarding. No hardware changes required." },
                        ].map((item, idx) => (
                            <AnimatedSection key={idx} delay={idx * 0.05}>
                                <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-white mb-2">{item.q}</h3>
                                    <p className="text-zinc-400">{item.a}</p>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* Internal Links */}
            <section className="py-16 border-t border-zinc-800">
                <div className="max-w-7xl mx-auto px-6">
                    <h3 className="text-lg font-bold text-zinc-500 mb-6 text-center">Explore More</h3>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/ai-towing-dispatch" className="px-6 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 hover:border-violet-500/50 hover:text-white transition">AI Towing Dispatch</Link>
                        <Link to="/after-hours-tow-answering-service" className="px-6 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 hover:border-violet-500/50 hover:text-white transition">After Hours Dispatch</Link>
                        <Link to="/towing-dispatch-software-comparison" className="px-6 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 hover:border-violet-500/50 hover:text-white transition">Software Comparison</Link>
                        <Link to="/web-forms" className="px-6 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 hover:border-violet-500/50 hover:text-white transition">Web Forms</Link>
                        <Link to="/blog" className="px-6 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 hover:border-violet-500/50 hover:text-white transition">Blog</Link>
                        <Link to="/pricing" className="px-6 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 hover:border-violet-500/50 hover:text-white transition">Pricing</Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 bg-black border-t border-zinc-800">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <Link to="/"><img src="/towmatic-logo-white.png" alt="Towmatic" className="h-8" /></Link>
                        <div className="flex items-center gap-6 text-sm text-zinc-500">
                            <Link to="/privacy-policy" className="hover:text-white transition">Privacy</Link>
                            <Link to="/terms-and-conditions" className="hover:text-white transition">Terms</Link>
                            <Link to="/contact" className="hover:text-white transition">Contact</Link>
                            <Link to="/blog" className="hover:text-white transition">Blog</Link>
                        </div>
                        <p className="text-zinc-600 text-sm">© {new Date().getFullYear()} Towmatic. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
